import { useTracking } from '@eventbrite/ads';
import { Carousel } from '@eventbrite/eds-carousel';
import { FormattedEvent } from '@eventbrite/event-renderer';
import React, { useContext, useRef } from 'react';

import {
    EventCardSaveAction,
    EventCardShareAction,
    getShareActionProps,
} from '@eventbrite/event-renderer';
import { logEvent } from '@eventbrite/statsig';
import { isEmpty } from 'lodash';

import classNames from 'classnames';
import {
    RELATED_EVENTS_GRID_EXPERIMENT,
    STATSIG_EVENT_NAMES,
} from '../../../constants';
import { TrackingContext } from '../../../contexts';
import {
    label,
    ShareType,
    trackShareAction,
} from '../../../tracking/trackShareAction';
import { AffiliateCodes, GACodes } from '../../../types';
import { useGetStatsigEnrollment } from '../../hooks/useGetStatsigVariant';
import { ListingsEventCard } from '../../_shared/EventCard/ListingsEventCard';
import { aggregateContext } from '../tracking';
import { RelatedEventsProps } from '../types';
import styles from './OtherEventsYouMayLike.module.scss';
import { useOtherEventsYouMayLike } from './OtherEventsYouMayLikeData';

const DESKTOP_SLIDE_SIZE = 3;
const MOBILE_SLIDE_SIZE = 1;
const COMMON_CAROUSEL_PROPS = {
    hasTopControls: true,
    isNotContinuous: true,
    alwaysShowSideNavigation: true,
    usesNewStyle: true,
    fullWidth: true,
};

export type OtherEventsYouMayLikeContentProps = Pick<
    RelatedEventsProps,
    | 'isAuthenticated'
    | 'shareComponentProps'
    | 'eventId'
    | 'locale'
    | 'areMetricsEnabled'
> & {
    eventsToRemove?: Pick<FormattedEvent, 'id'>[];
};

export function OtherEventsYouMayLikeContent({
    isAuthenticated,
    areMetricsEnabled,
    shareComponentProps,
    eventId,
    eventsToRemove,
    locale,
}: OtherEventsYouMayLikeContentProps) {
    const currentPage = useRef({ number: 0, size: 0 });
    const context = useContext(TrackingContext);

    const sortedEvents = useOtherEventsYouMayLike({
        eventId,
        isAuthenticated,
        suspense: true,
        eventsToRemove,
    });

    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event: { ...event, id },
            events: sortedEvents,
            context,
            page: currentPage.current,
        }),
    );

    const { variant: isEventCardGridExperiment } = useGetStatsigEnrollment({
        name: RELATED_EVENTS_GRID_EXPERIMENT.experimentName,
        paramName: RELATED_EVENTS_GRID_EXPERIMENT.param,
        defaultValue: RELATED_EVENTS_GRID_EXPERIMENT.defaultValue,
    });

    const eventCardMap = getEventCardMap({
        events: sortedEvents,
        isAuthenticated,
        shareComponentProps,
        areMetricsEnabled,
        locale,
    });

    const wrapEventCardMapForDesktop = () => {
        const slides = [];

        for (let i = 0; i < eventCardMap.length; i += DESKTOP_SLIDE_SIZE) {
            const slideItem = eventCardMap.filter(
                (_, index) => index >= i && index < i + DESKTOP_SLIDE_SIZE,
            );
            slides.push(slideItem);
        }
        return slides;
    };

    const eventCardMapForDesktop = wrapEventCardMapForDesktop().map(
        (slide, index) => {
            return (
                <div className={styles.carouselDesktop} key={index}>
                    {slide}
                </div>
            );
        },
    );

    if (isEmpty(sortedEvents)) {
        return null;
    }

    if (isEventCardGridExperiment) {
        return (
            <section
                className={classNames(
                    styles.relatedEvents,
                    styles.relatedEventsGrid,
                )}
                ref={ref}
                style={{ marginTop: -48 }}
            >
                {eventCardMap}
            </section>
        );
    }

    return (
        <section
            className={styles.relatedEvents}
            ref={ref}
            style={{ marginTop: -48 }}
        >
            <div className="eds-show-up-mn" data-testid="carousel-desktop">
                <Carousel
                    onTransition={(toPage: number) => {
                        currentPage.current = {
                            number: toPage,
                            size: DESKTOP_SLIDE_SIZE,
                        };
                    }}
                    {...COMMON_CAROUSEL_PROPS}
                >
                    {eventCardMapForDesktop}
                </Carousel>
            </div>

            <div
                className="eds-show-down-sw carousel-mobile"
                data-testid="carousel-mobile"
            >
                <Carousel
                    onTransition={(toPage: number) => {
                        currentPage.current = {
                            number: toPage,
                            size: MOBILE_SLIDE_SIZE,
                        };
                    }}
                    {...COMMON_CAROUSEL_PROPS}
                >
                    {eventCardMap}
                </Carousel>
            </div>
        </section>
    );
}

export function getEventCardMap({
    events,
    isAuthenticated,
    shareComponentProps,
    areMetricsEnabled,
    locale,
}: {
    events: FormattedEvent[];
} & Pick<
    RelatedEventsProps,
    'locale' | 'areMetricsEnabled' | 'shareComponentProps' | 'isAuthenticated'
>): JSX.Element[] {
    return events.map((event: FormattedEvent) => {
        const affCode = AffiliateCodes.AFF_RELATED_EVENTS_LIVE;
        const eventKey = event.promotedListingMetadata?.adId || event.id;
        const shareActionProps = getShareActionProps({
            name: event.name,
            id: event.id,
            url: event.url,
            affCode: affCode,
            utmOptions: shareComponentProps.utmOptions,
            onShareItemClick: (id: string) =>
                trackShareAction(
                    label(id as ShareType),
                    GACodes.GA_CATEGORY_EVENT_CARD,
                ),
            onClick: () => {
                trackShareAction(
                    GACodes.GA_LABEL_OPEN_MODAL,
                    GACodes.GA_CATEGORY_EVENT_CARD,
                );
                if (areMetricsEnabled) {
                    logEvent(STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_SHARE);
                }
            },
        });

        return (
            <ListingsEventCard
                key={eventKey}
                locale={locale || 'en-US'}
                event={event}
                statsigLocationString={
                    event.isPromoted
                        ? 'Related Events - Event Carousel - Promoted'
                        : undefined
                }
                affCode={affCode}
                // TODO: This should be unified with the statsigLocationString
                onClick={() =>
                    areMetricsEnabled &&
                    logEvent(STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_CLICK)
                }
                actions={
                    <>
                        <EventCardSaveAction
                            isAuthenticated={!!isAuthenticated}
                            savedByYou={event.savedByYou}
                            id={event.id}
                            eventName={event.name}
                            imageUrl={event.imageUrl}
                            actionStyle="outline"
                            onClick={(saved) =>
                                areMetricsEnabled &&
                                saved &&
                                logEvent(
                                    STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_LIKE,
                                )
                            }
                        />
                        <EventCardShareAction
                            actionStyle="outline"
                            {...shareActionProps}
                        />
                    </>
                }
            />
        );
    });
}

export default OtherEventsYouMayLikeContent;
